import api from './api';
import {getIdentityFromJwt} from '@/utils/helpers';
import errors from "@/mixins/errors";

export class Auth {

    static TOKEN_KEY = 'AUTH_TOKEN';
    static REFRESH_TOKEN_KEY = 'AUTH_REFRESH_TOKEN';

    publicPages = ['login'];
    defaultPageRouteName = 'sale-offers';

    constructor(store, router) {
        this.store = store;
        this.router = router;
    }

    async init() {
        this.registerRouterGuard();

        try {
            await this.loginFromLocalStorage();
        } catch (error) {
            if (!error.response || 401 !== error.response.status) {
                console.error('Error in Auth.init():', error);
            }

            this.askForLogout(error);
        }
    }

    registerRouterGuard() {
        this.router.beforeEach((to, from, next) => {
            const authRequired = !this.publicPages.includes(to.name);
            const loggedIn = !!this.store.state.user.identity.token;

            if ('login' !== to.name && authRequired && !loggedIn) {
                next({
                    name: 'login',
                    query: {
                        redirectTo: to.fullPath
                    }
                });
            } else {
                 if ('/' === to.path || !this.isAllowed(to)) {
                     const identity = getIdentityFromJwt(localStorage.getItem(Auth.TOKEN_KEY), localStorage.getItem(Auth.REFRESH_TOKEN_KEY));
                     identity.roles = Object.values(identity.roles);
                     if (identity.roles.some(r=> ['ROLE_EDITOR'].includes(r))) {
                         next({name: 'page-contents'});
                     } else {
                         next({name: this.defaultPageRouteName});
                     }
                } else {
                    next();
                }
            }
        });
    }

    async loginWithCredentials(username, password) {
        const response = await api.post('/api/auth/login', {username, password});
        this.login(response.data.token, response.data.refreshToken);
        this.redirectToDefaultPage();
        await this.refCode();
        return response;
    }

    async loginFromLocalStorage() {
        let token = localStorage.getItem(Auth.TOKEN_KEY);
        let refreshToken = localStorage.getItem(Auth.REFRESH_TOKEN_KEY);

        if (!token || !refreshToken) {
            return;
        }

        const identity = getIdentityFromJwt(token, refreshToken);

        let time = (new Date()).getTime();
        let expirationTime = this.getExpirationTime(identity.expires);

        if (time >= expirationTime) {
            let data;

            try {
                data = await this.refresh(refreshToken);
            } catch (error) {
                if (!error.response || 401 !== error.response.status) {
                    console.error('Error in Auth.init():', error);
                }

                this.askForLogout(error);

                return;
            }

            token = data.token;
            refreshToken = data.refreshToken;
        }

        if (token && refreshToken) {
            this.login(token, refreshToken);
        }
    }

    login(token, refreshToken) {
        const identity = getIdentityFromJwt(token, refreshToken);

        identity.roles = Object.values(identity.roles);
        if (!identity.roles.some(r=> ['ROLE_ADMIN', 'ROLE_CALL_CENTER', 'ROLE_TRADER', 'ROLE_EDITOR'].includes(r))) {
            throw errors.errors.MISSING_ROLE;
        }
        clearTimeout(this.refreshTimeout);

        const time = (new Date()).getTime();
        const expirationTime = this.getExpirationTime(identity.expires);
        let timeout = expirationTime - time;

        if (timeout <= 0) {
            /**
             * Looks like the token has expiration time less than expected (less than previously or less than 1 minute).
             * @see Auth.getExpirationTime
             */
            timeout = 60000; // 1 minute is the smallest reasonable value.
        }

        this.refreshTimeout = setTimeout(async () => {
            let data;

            try {
                data = await this.refresh(localStorage.getItem(Auth.REFRESH_TOKEN_KEY));
            } catch (error) {
                console.warn('Calling askForLogout() due to an error.');
                this.askForLogout();
                return;
            }

            this.login(data.token, data.refreshToken);
        }, timeout);

        api.defaults.headers.common.Authorization = `Bearer ${identity.token}`;

        localStorage.setItem(Auth.TOKEN_KEY, token);
        localStorage.setItem(Auth.REFRESH_TOKEN_KEY, refreshToken);

        this.store.commit('user/login', identity);
    }

    askForLogout(error) {
        let msg;

        if (error && error.response && 401 === error.response.status) {
            msg = 'Wygląda na to, że obydwa tokeny wygasły - musisz się zalogować ponownie. ' +
                  'Czy mam Cię teraz przekierować na stronę logowania?';
        } else {
            msg = 'Wystąpił błąd podczas próby przedłużenia sesji. ' +
                  'Czy mam Cię teraz przekierować na stronę logowania?';
        }

        if (confirm(msg)) {
            this.logout();
        }
    }

    logout() {
        clearTimeout(this.refreshTimeout);
        delete api.defaults.headers.common.Authorization;
        localStorage.removeItem(Auth.TOKEN_KEY);
        localStorage.removeItem(Auth.REFRESH_TOKEN_KEY);
        this.store.commit('user/logout');
        this.router.push({name: 'login'})
            .catch((error) => {
                console.error('Error during redirection:', error);
            });
    }

    async refresh(refreshToken) {
        const response = await api.post('/api/auth/refresh', {refreshToken});

        return {
            token: response.data.token,
            refreshToken: response.data.refreshToken,
        }
    }

    async refCode() {
        const response = await api.get(`/api/users/${this.store.state.user.identity.userId}`);
        localStorage.setItem('code', response.data.code);
    }
    redirectToDefaultPage() {
        const redirectTo = this.router.app.$route.query.redirectTo;

        if (redirectTo) {
            this.router.push(redirectTo)
                .catch((error) => {
                    console.error('Error during redirection:', error);
                });
        } else {
            const identity = getIdentityFromJwt(localStorage.getItem(Auth.TOKEN_KEY), localStorage.getItem(Auth.REFRESH_TOKEN_KEY));

            identity.roles = Object.values(identity.roles);
            if (identity.roles.some(r=> ['ROLE_EDITOR'].includes(r))) {
                this.router.push({name: 'page-contents'})
                    .catch((error) => {
                        console.error('Error during redirection:', error);
                    });
            } else {
                this.router.push({name: this.defaultPageRouteName})
                    .catch((error) => {
                        console.error('Error during redirection:', error);
                    });
            }
        }
    }

    getExpirationTime(expires) {
        return (new Date(expires*1000 - 60000)).getTime();
    }

    isAllowed(route) {
        return !route.meta.roles || this.store.state.user.identity.roles.some(r=> route.meta.roles.includes(r));
    }
}